'use client';

import styles from './styles/modals.module.css';
import { COURSE_FIELD_RULES } from '@/config/courseFieldRules';

const {
  lessonTitle: LESSON_TITLE_RULES,
  lessonCoverage: LESSON_COVERAGE_RULES,
} = COURSE_FIELD_RULES;

function FieldCharHint({ value, min, max, overLimitMessage }) {
  const length = (value || '').length;

  if (length < min) {
    const needed = min - length;
    return (
      <div className={styles.charCounter}>
        {needed} more characters needed
      </div>
    );
  }

  if (length > max) {
    return (
      <div className={styles.charCounter}>
        {overLimitMessage}
      </div>
    );
  }

  const left = Math.max(0, max - length);

  return (
    <div className={styles.charCounter}>
      {left} characters left (Optional)
    </div>
  );
}

export default function LessonModal({
  isOpen,
  onClose,
  lessonName,
  setLessonName,
  lessonDescription,
  setLessonDescription,
  onSave,
  isEditing,
}) {
  if (!isOpen) return null;

  const titleLength = (lessonName || '').length;
  const descriptionLength = (lessonDescription || '').length;

  const titleMin = LESSON_TITLE_RULES.minLength;
  const titleMax = LESSON_TITLE_RULES.maxLength;
  const descMin = LESSON_COVERAGE_RULES.minLength;
  const descMax = LESSON_COVERAGE_RULES.maxLength;

  const isTitleTooShort = titleLength < titleMin;
  const isTitleTooLong = titleLength > titleMax;
  const isDescTooShort = descriptionLength < descMin;
  const isDescTooLong = descriptionLength > descMax;

  const isInvalid =
    isTitleTooShort ||
    isTitleTooLong ||
    isDescTooShort ||
    isDescTooLong;

  return (
    <div className={styles.modal} onClick={onClose}>
      <div
        className={styles.modalContent}
        onClick={(e) => e.stopPropagation()}
      >
        <span className={styles.close} onClick={onClose}>
          &times;
        </span>

        <h2>{isEditing ? 'Edit Lesson' : 'Add Lesson'}</h2>

        {/* Lesson Title */}
        <label className={styles.formLabel} htmlFor="lessonName">
          Lesson Name
        </label>
        <input
          type="text"
          id="lessonName"
          className={styles.formInput}
          value={lessonName}
          onChange={(e) => setLessonName(e.target.value)}
          required={LESSON_TITLE_RULES.required}
          minLength={LESSON_TITLE_RULES.minLength}
          maxLength={LESSON_TITLE_RULES.maxLength}
        />
        <FieldCharHint
          value={lessonName}
          min={LESSON_TITLE_RULES.minLength}
          max={LESSON_TITLE_RULES.maxLength}
          overLimitMessage="Too many text — please shorten your lesson title."
        />

        {/* Lesson Description */}
        <label className={styles.formLabel} htmlFor="lessonDescription">
          Description
        </label>
        <textarea
          id="lessonDescription"
          className={styles.formInput}
          value={lessonDescription}
          onChange={(e) => setLessonDescription(e.target.value)}
          style={{ whiteSpace: 'pre-wrap' }}
          required={LESSON_COVERAGE_RULES.required}
          minLength={LESSON_COVERAGE_RULES.minLength}
          maxLength={LESSON_COVERAGE_RULES.maxLength}
        />
        <FieldCharHint
          value={lessonDescription}
          min={LESSON_COVERAGE_RULES.minLength}
          max={LESSON_COVERAGE_RULES.maxLength}
          overLimitMessage="Too many text — please shorten your lesson description."
        />

        {/* Actions */}
        <button
          className={`${styles.btn} ${styles.btnPrimary}`}
          onClick={onSave}
          disabled={isInvalid}
          style={{
            marginTop: '1rem',
            opacity: isInvalid ? 0.6 : 1,
            cursor: isInvalid ? 'not-allowed' : 'pointer',
          }}
        >
          {isTitleTooLong || isDescTooLong
            ? 'Too many details'
            : isTitleTooShort || isDescTooShort
            ? 'Need more details'
            : 'Save'}
        </button>
      </div>
    </div>
  );
}